import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../../hooks/usePageTitle';
import { FormSection, FieldRow, controlClass } from '../../components/FormSection';
import { Button } from '../../components/Button';
import { useCompleteOnboarding } from './hooks';

type Form = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  phone: string;
};

export function OnboardingProfilePage() {
  usePageTitle('Finish setup');
  const nav = useNavigate();
  const complete = useCompleteOnboarding();
  const [f, setF] = useState<Form>({
    addressLine1: '',
    addressLine2: '',
    city: '',
    region: '',
    postalCode: '',
    country: '',
    phone: ''
  });

  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setF((s) => ({ ...s, [k]: e.target.value }));

  const canSubmit =
    f.addressLine1.trim() && f.city.trim() && f.region.trim() && f.postalCode.trim() && f.country.trim();

  const onSubmit = async () => {
    await complete.mutateAsync({
      addressLine1: f.addressLine1.trim(),
      addressLine2: f.addressLine2.trim() || undefined,
      city: f.city.trim(),
      region: f.region.trim(),
      postalCode: f.postalCode.trim(),
      country: f.country.trim(),
      phone: f.phone.trim() || undefined
    });
    nav('/dashboard');
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-10 space-y-5">
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink">Finish your business profile</h1>
        <p className="text-slate-500 text-sm mt-1">
          Your business address appears on documents and is required to send email. This is the last step.
        </p>
      </div>
      <FormSection title="Business address">
        <FieldRow label="Address line 1" htmlFor="a1">
          <input id="a1" className={controlClass} value={f.addressLine1} onChange={set('addressLine1')} />
        </FieldRow>
        <FieldRow label="Address line 2" htmlFor="a2">
          <input id="a2" className={controlClass} value={f.addressLine2} onChange={set('addressLine2')} />
        </FieldRow>
        <FieldRow label="City" htmlFor="city">
          <input id="city" className={controlClass} value={f.city} onChange={set('city')} />
        </FieldRow>
        <FieldRow label="Region/State" htmlFor="region">
          <input id="region" className={controlClass} value={f.region} onChange={set('region')} />
        </FieldRow>
        <FieldRow label="Postal code" htmlFor="pc">
          <input id="pc" className={controlClass} value={f.postalCode} onChange={set('postalCode')} />
        </FieldRow>
        <FieldRow label="Country" htmlFor="ctry">
          <input id="ctry" className={controlClass} value={f.country} onChange={set('country')} />
        </FieldRow>
        <FieldRow label="Phone (optional)" htmlFor="ph">
          <input id="ph" className={controlClass} value={f.phone} onChange={set('phone')} />
        </FieldRow>
      </FormSection>
      {complete.isError && (
        <p className="text-sm text-danger">Couldn't finish — please check the fields and try again.</p>
      )}
      <div className="flex justify-end">
        <Button disabled={!canSubmit || complete.isPending} onClick={onSubmit}>
          {complete.isPending ? 'Finishing…' : 'Finish setup'}
        </Button>
      </div>
    </div>
  );
}
